import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SimpleFileLink } from '@texas/api/endpoints/filesApi';
import { complianceDocumentKindLabel } from '@texas/components/configuration/complianceDocumentDefinitions/translations';
import {
  SupplierComplianceDocument,
  supplierComplianceDocumentsApi,
} from '@texas/api/endpoints/supplierComplianceDocumentsApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { EntityDirectoryEmptyState } from '@texas/components/shared/entityDirectory/EntityDirectoryEmptyState';
import { EntityDirectoryFileThumbnail } from '@texas/components/shared/entityDirectory/EntityDirectoryFileThumbnail';
import { EntityDirectoryFilterBar } from '@texas/components/shared/entityDirectory/EntityDirectoryFilterBar';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';
import { RoleRestrict } from '@texas/components/shared/RoleRestrict';
import { useActiveSession } from '@texas/hooks/useSession';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { CreateSupplierComplianceDocument } from './CreateSupplierComplianceDocument';
import { UpdateSupplierComplianceDocument } from './UpdateSupplierComplianceDocument';
import { supplierComplianceDocumentColumns } from './columns';
import { supplierComplianceDocumentEvents } from './events';

interface Props {
  supplierId: number;
  documentsFolderId: number;
}

interface DocumentGroup {
  id: number;
  name: string;
  logo: SimpleFileLink | null;
  documents: SupplierComplianceDocument[];
}

export function SupplierComplianceDocuments({
  supplierId,
  documentsFolderId,
}: Props) {
  const { t } = useTranslation();
  const { branches } = useActiveSession();
  const createDisclosure = useDisclosure();
  const {
    value: editingDocument,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<SupplierComplianceDocument>();
  const [searchTerm, setSearchTerm] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data, refetch, loading, error } = useApiResource(
    supplierComplianceDocumentsApi.search,
  );

  useEffect(() => {
    refetch({ supplierId, searchTerm, includeArchived });
  }, [refetch, supplierId, searchTerm, includeArchived]);

  useEffect(() => {
    return supplierComplianceDocumentEvents.changed.subscribe(
      (changedSupplierId) => {
        if (changedSupplierId !== supplierId) return;
        refetch({ supplierId, searchTerm, includeArchived });
      },
    );
  }, [includeArchived, refetch, searchTerm, supplierId]);

  const groups = useMemo(() => {
    const byDefinition = new Map<number, DocumentGroup>();
    for (const document of data?.items ?? []) {
      const group = byDefinition.get(document.complianceDocumentDefinitionId);
      if (group) {
        group.documents.push(document);
      } else {
        byDefinition.set(document.complianceDocumentDefinitionId, {
          id: document.complianceDocumentDefinitionId,
          name: document.definitionName,
          logo: document.definitionLogo,
          documents: [document],
        });
      }
    }
    return Array.from(byDefinition.values());
  }, [data]);

  const columns = useMemo(
    () =>
      supplierComplianceDocumentColumns({
        t,
        onEdit: onEditOpen,
        branches,
      }),
    [branches, onEditOpen, t],
  );

  return (
    <BodyOverride>
      <Box
        color="entityDirectory.text"
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <Flex
          align={{ base: 'stretch', sm: 'flex-start' }}
          direction={{ base: 'column', sm: 'row' }}
          gap={3}
          justify="space-between"
        >
          <Flex direction="column" gap={1}>
            <Text variant="entity-directory-heading">
              {t('complianceDocumentDefinitions.title')}
            </Text>
            <Text variant="entity-directory-description">
              {t('suppliers.workspace.documentsDescription')}
            </Text>
          </Flex>
          <RoleRestrict
            allow={[Role.SystemAdministrator, Role.InternalAdministrator]}
          >
            <Button
              variant="entityDirectoryPrimary"
              onClick={createDisclosure.onOpen}
            >
              {t('suppliers.workspace.newDocument')}
            </Button>
          </RoleRestrict>
        </Flex>
        <RoleRestrict
          allow={[Role.SystemAdministrator, Role.InternalAdministrator]}
        >
          <CreateSupplierComplianceDocument
            supplierId={supplierId}
            documentsFolderId={documentsFolderId}
            branches={branches}
            isOpen={createDisclosure.isOpen}
            onClose={createDisclosure.onClose}
          />
          <UpdateSupplierComplianceDocument
            supplierId={supplierId}
            documentsFolderId={documentsFolderId}
            document={editingDocument}
            isOpen={isEditOpen}
            onClose={onEditClose}
          />
        </RoleRestrict>
        <EntityDirectoryFilterBar
          actions={
            <TexasSwitch
              checked={!includeArchived}
              label={t('complianceDocumentDefinitions.hideArchived')}
              labelColor="entityDirectory.text"
              onChange={(hidden) => setIncludeArchived(!hidden)}
            />
          }
        >
          <Box
            width={{ base: '100%', sm: '340px' }}
            sx={{
              '.chakra-input__group': {
                borderWidth: '1px',
                borderColor: 'entityDirectory.border',
              },
            }}
          >
            <SearchFilterInput
              style="entityDirectory"
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('suppliers.workspace.searchDocuments')}
            />
          </Box>
        </EntityDirectoryFilterBar>
        {loading && <Spinner />}
        <ErrorDetails error={error} />
        {!loading && !error && groups.length === 0 && (
          <EntityDirectoryEmptyState p={6} textAlign="left">
            {t('suppliers.workspace.noDocuments')}
          </EntityDirectoryEmptyState>
        )}
        {groups.length > 0 && (
          <Accordion
            allowMultiple={true}
            defaultIndex={groups.map((_, index) => index)}
            variant="entityDirectory"
          >
            {groups.map((group) => (
              <AccordionItem key={group.id} border="none">
                <AccordionButton>
                  <AccordionIcon />
                  <EntityDirectoryFileThumbnail
                    alt={group.name}
                    file={group.logo}
                    size="36px"
                  />
                  <Flex
                    align="center"
                    flex={1}
                    flexWrap="wrap"
                    gap={3}
                    textAlign="left"
                  >
                    <Text>{group.name}</Text>
                    <Badge variant="configuration-reference">
                      {t('suppliers.workspace.documentCount', {
                        count: group.documents.length,
                      })}
                    </Badge>
                    <Text
                      fontSize="12px"
                      variant="entity-directory-description"
                    >
                      {complianceDocumentKindLabel(
                        group.documents[0].definitionKind,
                        t,
                      )}
                    </Text>
                  </Flex>
                </AccordionButton>
                <AccordionPanel>
                  <DataTableContainer
                    error={null}
                    showColumnToggle={false}
                    sx={{
                      table: { border: 'none' },
                    }}
                    datatable={{
                      columns,
                      data: group.documents,
                      rowProps: {
                        getRowId: (document) => document.id.toString(),
                      },
                      variant: 'entityDirectoryCompact',
                    }}
                  />
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </Box>
    </BodyOverride>
  );
}
